import React, { useState, forwardRef, useImperativeHandle, useMemo, useCallback, useRef, useEffect } from 'react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { ChevronDown, ChevronUp, Circle, Plus, GripVertical } from 'lucide-react'
import DataGrid from '@/components/data/DataGrid'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceTreeNode, PerformanceHierarchyTableRef } from '../types'
import { getPerformanceTypeLabel } from '../utils/performanceUtils'
import { PerformanceGroupService } from '@/api/generated/services/PerformanceGroupService'
import { PerformanceCategoryService } from '@/api/generated/services/PerformanceCategoryService'
import { PerformanceClassService } from '@/api/generated/services/PerformanceClassService'
import { PerformanceMapService } from '@/api/generated/services/PerformanceMapService'
import { useAlert } from '@/components/ui/Alert/exports'


interface PerformanceHierarchyTableProps {
  data: PerformanceTreeNode[]
  loading?: boolean
  error?: string | null
  onRowClick?: (performance: PerformanceTreeNode) => void
  onCreatePerformanceIndicator?: (businessId: string) => void
  onCreateCategory?: (parentId: string) => void
  onCreatePerformanceClass?: (categoryId: string) => void
  onEditPerformanceClass?: (classId: string) => void
  onAddPerformance?: (parentId: string) => void
  onSetActionButtonClicked?: (clicked: boolean) => void
  /** 순서 변경을 위한 데이터 업데이트 콜백 */
  onDataUpdate?: (newData: PerformanceTreeNode[]) => void
}

// Drag State 타입 정의
interface DragState {
  isDragging: boolean
  draggedItem: PerformanceTreeNode | null
  draggedItemType: string | null
  draggedItemParentId: string | null
  dropTargetId: string | null
  dropPosition: 'before' | 'after' | null
}

const PerformanceHierarchyTable = forwardRef<PerformanceHierarchyTableRef, PerformanceHierarchyTableProps>(
  ({
    data = [],
    loading = false,
    error = null,
    onRowClick,
    onCreatePerformanceIndicator,
    onCreateCategory,
    onCreatePerformanceClass,
    onEditPerformanceClass,
    onAddPerformance,
    onSetActionButtonClicked,
    onDataUpdate
  }, ref) => {
    const { showSuccess, showError } = useAlert()
    const [collapsedItems, setCollapsedItems] = useState<Set<string>>(new Set())

    // Drag & Drop State
    const [dragState, setDragState] = useState<DragState>({
      isDragging: false,
      draggedItem: null,
      draggedItemType: null,
      draggedItemParentId: null,
      dropTargetId: null,
      dropPosition: null
    })

    // Ref for drag data (데이터 손실 방지)
    const dragDataRef = useRef<PerformanceTreeNode | null>(null)

    // Ref for auto-scroll interval
    const scrollIntervalRef = useRef<number | null>(null)

    // Ref for grid container (스크롤 이벤트용)
    const gridContainerRef = useRef<HTMLDivElement | null>(null)

    // 현재 드롭 대상 추적용 ref
    const currentDropTargetRef = useRef<{ targetId: string | null, position: 'before' | 'after' | null }>({
      targetId: null,
      position: null
    })

    // Expand all items
    const expandAll = useCallback(() => {
      setCollapsedItems(new Set())
    }, [])

    // Toggle collapse/expand for an item
    const toggleCollapse = useCallback((itemId: string) => {
      setCollapsedItems(prev => {
        const newSet = new Set(prev)
        if (newSet.has(itemId)) {
          newSet.delete(itemId)
        } else {
          newSet.add(itemId)
        }
        return newSet
      })
    }, [])

    // Check if an item should be hidden due to collapsed parent
    const isItemHidden = useCallback((orgHierarchy: string[]) => {
      for (let i = 0; i < orgHierarchy.length - 1; i++) {
        const parentPath = orgHierarchy.slice(0, i + 1).join('|')
        if (collapsedItems.has(parentPath)) {
          return true
        }
      }
      return false
    }, [collapsedItems])

    // Check if item has children
    const hasChildren = useCallback((item: PerformanceTreeNode, allData: PerformanceTreeNode[]) => {
      return allData.some(dataItem =>
        dataItem.orgHierarchy.length === item.orgHierarchy.length + 1 &&
        dataItem.orgHierarchy.slice(0, item.orgHierarchy.length).join('|') === item.orgHierarchy.join('|')
      )
    }, [])

    // Transform hierarchical data to flat structure for AG-Grid
    const treeData = useMemo(() => {
      const flatData: PerformanceTreeNode[] = []

      const traverse = (node: PerformanceTreeNode, path: string[] = []) => {
        const currentPath = [...path, node.name]
        const nodeWithPath = {
          ...node,
          orgHierarchy: currentPath
        }
        flatData.push(nodeWithPath)

        if (node.children && node.children.length > 0) {
          node.children.forEach(child => traverse(child, currentPath))
        }
      }

      data.forEach(node => traverse(node))
      return flatData
    }, [data])

    // Filter data based on collapsed state
    const filteredData = useMemo(() => {
      return treeData.filter(item => !isItemHidden(item.orgHierarchy))
    }, [treeData, isItemHidden])

    // Collapse all items that have children
    const collapseAll = useCallback(() => {
      const allItemIds = new Set<string>()
      treeData.forEach(item => {
        if (hasChildren(item, treeData)) {
          allItemIds.add(item.orgHierarchy.join('|'))
        }
      })
      setCollapsedItems(allItemIds)
    }, [treeData, hasChildren])

    useImperativeHandle(ref, () => ({
      refreshData: () => {},
      expandAll,
      collapseAll
    }))

    // ===== Drag & Drop 유틸리티 함수 =====

    // 부모 ID 가져오기
    const getParentId = useCallback((item: PerformanceTreeNode): string | null => {
      switch (item.type) {
        case 'performance_group':
          return item.businessId || null
        case 'performance_category':
          // 부모가 group이면 groupId, 부모가 category면 parentId
          return item.parentId || item.groupId || null
        case 'performance_class':
          return item.performance_category_id || null
        case 'performance':
          // treeData에서 이 performance의 부모 class를 찾아야 함
          for (const node of treeData) {
            if (node.type === 'performance_class') {
              // orgHierarchy로 부모 관계 확인
              if (item.orgHierarchy.length === node.orgHierarchy.length + 1 &&
                  item.orgHierarchy.slice(0, node.orgHierarchy.length).join('|') === node.orgHierarchy.join('|')) {
                return node.id
              }
            }
          }
          return null
        default:
          return null
      }
    }, [treeData])

    // 드롭 대상 유효성 검사
    const validateDropTarget = useCallback((
      draggedItem: PerformanceTreeNode,
      targetItem: PerformanceTreeNode
    ): boolean => {
      // 같은 유형인지 확인
      if (draggedItem.type !== targetItem.type) return false

      // 자기 자신인지 확인
      if (draggedItem.id === targetItem.id) return false

      // 같은 상위 범위인지 확인
      const draggedParentId = getParentId(draggedItem)
      const targetParentId = getParentId(targetItem)

      return draggedParentId === targetParentId
    }, [getParentId])

    // 같은 부모 내 형제 항목들 가져오기
    const getSiblingsInParent = useCallback((
      item: PerformanceTreeNode,
      allItems: PerformanceTreeNode[]
    ): PerformanceTreeNode[] => {
      const parentId = getParentId(item)
      return allItems.filter(sibling =>
        sibling.type === item.type &&
        getParentId(sibling) === parentId
      )
    }, [getParentId])

    // 인접 항목 ID 계산
    const calculateAdjacentIds = useCallback((
      draggedItem: PerformanceTreeNode,
      targetItem: PerformanceTreeNode,
      dropPosition: 'before' | 'after'
    ): { prevItemId: string | number | null, nextItemId: string | number | null } => {
      const siblings = getSiblingsInParent(draggedItem, treeData)
        .filter(s => s.id !== draggedItem.id)

      const targetIndex = siblings.findIndex(s => s.id === targetItem.id)

      // performance 유형의 경우 map_id 사용 (API가 number 타입 요구)
      const getItemIdForApi = (item: PerformanceTreeNode): string | number | null => {
        if (item.type === 'performance') {
          return item.map_id ?? null
        }
        return item.id
      }

      if (dropPosition === 'before') {
        const prevItem = targetIndex > 0 ? siblings[targetIndex - 1] : null
        return {
          prevItemId: prevItem ? getItemIdForApi(prevItem) : null,
          nextItemId: getItemIdForApi(targetItem)
        }
      } else {
        const nextItem = targetIndex < siblings.length - 1 ? siblings[targetIndex + 1] : null
        return {
          prevItemId: getItemIdForApi(targetItem),
          nextItemId: nextItem ? getItemIdForApi(nextItem) : null
        }
      }
    }, [getSiblingsInParent, treeData])

    // API 호출 함수
    const callSortApi = useCallback(async (
      item: PerformanceTreeNode,
      prevId: string | number | null,
      nextId: string | number | null
    ): Promise<void> => {
      switch (item.type) {
        case 'performance_group':
          await PerformanceGroupService.appsPerformanceViewsUpdatePerformanceGroupSort({
            groupId: item.id,
            requestBody: {
              prev_group_id: prevId as string | null,
              next_group_id: nextId as string | null
            }
          })
          break

        case 'performance_category':
          await PerformanceCategoryService.appsPerformanceViewsUpdatePerformanceCategorySort({
            categoryId: item.id,
            requestBody: {
              prev_category_id: prevId as string | null,
              next_category_id: nextId as string | null
            }
          })
          break

        case 'performance_class':
          await PerformanceClassService.appsPerformanceViewsUpdatePerformanceClassSort({
            classId: item.id,
            requestBody: {
              prev_class_id: prevId as string | null,
              next_class_id: nextId as string | null
            }
          })
          break

        case 'performance':
          // map_id 사용 (API는 number 타입 요구)
          if (!item.map_id) {
            throw new Error('map_id not found for performance')
          }
          const mapId = parseInt(item.map_id, 10)
          if (isNaN(mapId)) {
            throw new Error(`Invalid map_id: ${item.map_id}`)
          }
          await PerformanceMapService.appsPerformanceViewsUpdatePerformanceMapSort({
            mapId: mapId,
            requestBody: {
              prev_map_id: prevId ? parseInt(String(prevId), 10) : null,
              next_map_id: nextId ? parseInt(String(nextId), 10) : null
            }
          })
          break

        default:
          throw new Error(`Unsupported type: ${item.type}`)
      }
    }, [])

    // 로컬 데이터 재정렬 함수
    const reorderLocalData = useCallback((
      draggedItem: PerformanceTreeNode,
      targetItem: PerformanceTreeNode,
      dropPosition: 'before' | 'after'
    ): PerformanceTreeNode[] => {
      // Deep clone
      const newData = JSON.parse(JSON.stringify(data)) as PerformanceTreeNode[]

      // 재귀적으로 children 배열에서 재정렬 수행
      const reorderInChildren = (nodes: PerformanceTreeNode[]): boolean => {
        for (const node of nodes) {
          if (node.children && node.children.length > 0) {
            const draggedIdx = node.children.findIndex(c => c.id === draggedItem.id)
            const targetIdx = node.children.findIndex(c => c.id === targetItem.id)

            if (draggedIdx !== -1 && targetIdx !== -1) {
              const [removed] = node.children.splice(draggedIdx, 1)
              const newIdx = dropPosition === 'before'
                ? (draggedIdx < targetIdx ? targetIdx - 1 : targetIdx)
                : (draggedIdx < targetIdx ? targetIdx : targetIdx + 1)
              node.children.splice(newIdx, 0, removed)
              return true
            }

            if (reorderInChildren(node.children)) return true
          }
        }
        return false
      }

      reorderInChildren(newData)
      return newData
    }, [data])

    // ===== 드래그 이벤트 핸들러 =====

    // 드래그 클래스 적용 함수
    const applyDragClasses = useCallback((draggedItem: PerformanceTreeNode) => {
      const allRows = document.querySelectorAll('.ag-row[row-id]')
      allRows.forEach(rowEl => {
        const rowId = rowEl.getAttribute('row-id')
        if (!rowId) return

        const rowData = filteredData.find(d => d.id === rowId)
        if (!rowData) return

        if (rowData.id === draggedItem.id) {
          if (!rowEl.classList.contains('row-dragging')) {
            rowEl.classList.add('row-dragging')
          }
          return
        }

        const isValidTarget = validateDropTarget(draggedItem, rowData)
        if (!isValidTarget) {
          if (!rowEl.classList.contains('drag-disabled')) {
            rowEl.classList.add('drag-disabled')
          }
        } else {
          rowEl.classList.remove('drag-disabled')
        }
      })
    }, [filteredData, validateDropTarget])

    // 드롭 대상 스타일 직접 조작 함수
    const updateDropTargetStyle = useCallback((targetId: string | null, position: 'before' | 'after' | null) => {
      if (currentDropTargetRef.current.targetId === targetId &&
          currentDropTargetRef.current.position === position) {
        return
      }

      currentDropTargetRef.current = { targetId, position }

      document.querySelectorAll('.ag-row.drop-target-before, .ag-row.drop-target-after').forEach(el => {
        el.classList.remove('drop-target-before', 'drop-target-after')
      })

      if (targetId && position) {
        const targetRow = document.querySelector(`[row-id="${targetId}"]`)
        if (targetRow) {
          targetRow.classList.add(`drop-target-${position}`)
        }
      }
    }, [])

    const handleDragStart = useCallback((item: PerformanceTreeNode) => {
      // 이전 드래그 상태 정리
      document.querySelectorAll('.ag-row.row-dragging, .ag-row.drop-target-before, .ag-row.drop-target-after, .ag-row.drag-disabled').forEach(el => {
        el.classList.remove('row-dragging', 'drop-target-before', 'drop-target-after', 'drag-disabled')
      })

      dragDataRef.current = item
      currentDropTargetRef.current = { targetId: null, position: null }
      const parentId = getParentId(item)

      applyDragClasses(item)

      setDragState({
        isDragging: true,
        draggedItem: item,
        draggedItemType: item.type,
        draggedItemParentId: parentId,
        dropTargetId: null,
        dropPosition: null
      })
    }, [getParentId, applyDragClasses])

    const handleDragEnd = useCallback(() => {
      dragDataRef.current = null
      currentDropTargetRef.current = { targetId: null, position: null }

      if (scrollIntervalRef.current) {
        cancelAnimationFrame(scrollIntervalRef.current)
        scrollIntervalRef.current = null
      }

      document.querySelectorAll('.ag-row.row-dragging, .ag-row.drop-target-before, .ag-row.drop-target-after, .ag-row.drag-disabled').forEach(el => {
        el.classList.remove('row-dragging', 'drop-target-before', 'drop-target-after', 'drag-disabled')
      })

      setDragState({
        isDragging: false,
        draggedItem: null,
        draggedItemType: null,
        draggedItemParentId: null,
        dropTargetId: null,
        dropPosition: null
      })
    }, [])

    // 그리드 컨테이너에서 row-id로 데이터 찾기
    const findRowDataFromElement = useCallback((element: HTMLElement): PerformanceTreeNode | null => {
      const rowElement = element.closest('.ag-row') as HTMLElement
      if (!rowElement) return null

      const rowId = rowElement.getAttribute('row-id')
      if (!rowId) return null

      return filteredData.find(item => item.id === rowId) || null
    }, [filteredData])

    // 자동 스크롤 처리 함수
    const handleAutoScroll = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      const gridContainer = e.currentTarget
      const viewport = gridContainer.querySelector('.ag-body-viewport') as HTMLElement
      if (!viewport) return

      const viewportRect = viewport.getBoundingClientRect()
      const mouseY = e.clientY

      const scrollZone = 120
      const baseScrollSpeed = 15

      if (scrollIntervalRef.current) {
        cancelAnimationFrame(scrollIntervalRef.current)
        scrollIntervalRef.current = null
      }

      if (mouseY < viewportRect.top + scrollZone && mouseY > viewportRect.top) {
        const intensity = 1 - (mouseY - viewportRect.top) / scrollZone
        const scrollSpeed = baseScrollSpeed * (0.3 + intensity * 0.7)
        const scroll = () => {
          viewport.scrollTop -= scrollSpeed
          const draggedItem = dragDataRef.current
          if (draggedItem) {
            applyDragClasses(draggedItem)
            scrollIntervalRef.current = requestAnimationFrame(scroll)
          }
        }
        scrollIntervalRef.current = requestAnimationFrame(scroll)
      } else if (mouseY > viewportRect.bottom - scrollZone && mouseY < viewportRect.bottom) {
        const intensity = 1 - (viewportRect.bottom - mouseY) / scrollZone
        const scrollSpeed = baseScrollSpeed * (0.3 + intensity * 0.7)
        const scroll = () => {
          viewport.scrollTop += scrollSpeed
          const draggedItem = dragDataRef.current
          if (draggedItem) {
            applyDragClasses(draggedItem)
            scrollIntervalRef.current = requestAnimationFrame(scroll)
          }
        }
        scrollIntervalRef.current = requestAnimationFrame(scroll)
      }
    }, [applyDragClasses])

    // 그리드 레벨 드래그 오버 핸들러
    const handleGridDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      const draggedItem = dragDataRef.current
      if (!draggedItem) return

      handleAutoScroll(e)
      applyDragClasses(draggedItem)

      const targetData = findRowDataFromElement(e.target as HTMLElement)
      if (!targetData || targetData.type === 'business') {
        e.dataTransfer.dropEffect = 'none'
        updateDropTargetStyle(null, null)
        return
      }

      const isValid = validateDropTarget(draggedItem, targetData)
      if (!isValid) {
        e.dataTransfer.dropEffect = 'none'
        updateDropTargetStyle(null, null)
        setDragState(prev => ({
          ...prev,
          dropTargetId: null,
          dropPosition: null
        }))
        return
      }

      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'

      const rowElement = (e.target as HTMLElement).closest('.ag-row') as HTMLElement
      if (rowElement) {
        const rect = rowElement.getBoundingClientRect()
        const midY = rect.top + rect.height / 2
        const dropPosition: 'before' | 'after' = e.clientY < midY ? 'before' : 'after'

        updateDropTargetStyle(targetData.id, dropPosition)

        setDragState(prev => ({
          ...prev,
          dropTargetId: targetData.id,
          dropPosition
        }))
      }
    }, [findRowDataFromElement, validateDropTarget, updateDropTargetStyle, handleAutoScroll, applyDragClasses])

    // 순서 변경 여부 확인 함수
    const isPositionChanged = useCallback((
      draggedItem: PerformanceTreeNode,
      targetItem: PerformanceTreeNode,
      dropPosition: 'before' | 'after'
    ): boolean => {
      const siblings = getSiblingsInParent(draggedItem, treeData)
      const draggedIndex = siblings.findIndex(s => s.id === draggedItem.id)
      const targetIndex = siblings.findIndex(s => s.id === targetItem.id)

      if (draggedIndex === -1 || targetIndex === -1) return true

      if (dropPosition === 'before' && targetIndex === draggedIndex + 1) {
        return false
      }

      if (dropPosition === 'after' && targetIndex === draggedIndex - 1) {
        return false
      }

      return true
    }, [getSiblingsInParent, treeData])

    // 그리드 레벨 드롭 핸들러
    const handleGridDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()

      const draggedItem = dragDataRef.current
      if (!draggedItem || !dragState.dropPosition || !dragState.dropTargetId) {
        handleDragEnd()
        return
      }

      const targetData = filteredData.find(item => item.id === dragState.dropTargetId)
      if (!targetData) {
        handleDragEnd()
        return
      }

      if (!validateDropTarget(draggedItem, targetData)) {
        handleDragEnd()
        return
      }

      if (!isPositionChanged(draggedItem, targetData, dragState.dropPosition)) {
        handleDragEnd()
        return
      }

      // 이전 데이터 백업
      const previousData = JSON.parse(JSON.stringify(data))

      // 인접 ID 계산
      const { prevItemId, nextItemId } = calculateAdjacentIds(
        draggedItem,
        targetData,
        dragState.dropPosition
      )

      // 로컬 데이터 먼저 업데이트 (낙관적 업데이트)
      const newData = reorderLocalData(draggedItem, targetData, dragState.dropPosition)
      onDataUpdate?.(newData)

      try {
        await callSortApi(draggedItem, prevItemId, nextItemId)
        showSuccess('순서가 변경되었습니다.')
      } catch (error) {
        onDataUpdate?.(previousData)
        showError('순서 변경에 실패했습니다.')
        console.error('Sort API error:', error)
      } finally {
        handleDragEnd()
      }
    }, [dragState.dropPosition, dragState.dropTargetId, filteredData, data, validateDropTarget, isPositionChanged, calculateAdjacentIds, reorderLocalData, onDataUpdate, callSortApi, handleDragEnd, showSuccess, showError])

    // 그리드 레벨 드래그 리브 핸들러
    const handleGridDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      const relatedTarget = e.relatedTarget as HTMLElement
      const gridContainer = e.currentTarget as HTMLElement

      if (!relatedTarget || !gridContainer.contains(relatedTarget)) {
        updateDropTargetStyle(null, null)
        setDragState(prev => ({
          ...prev,
          dropTargetId: null,
          dropPosition: null
        }))
      }
    }, [updateDropTargetStyle])

    // 스크롤 시 드래그 클래스 재적용
    useEffect(() => {
      const container = gridContainerRef.current
      if (!container) return

      const viewport = container.querySelector('.ag-body-viewport') as HTMLElement
      if (!viewport) return

      const handleScroll = () => {
        const draggedItem = dragDataRef.current
        if (draggedItem) {
          applyDragClasses(draggedItem)
        }
      }

      viewport.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        viewport.removeEventListener('scroll', handleScroll)
      }
    }, [applyDragClasses])

    // ===== Gripper Renderer =====
    const GripperRenderer = useCallback((params: ICellRendererParams<PerformanceTreeNode>) => {
      const item = params.data

      // business 타입은 gripper 숨김
      if (!item || item.type === 'business') {
        return null
      }

      return (
        <div
          className="gripper-handle"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/plain', item.id)
            handleDragStart(item)
          }}
          onDragEnd={handleDragEnd}
        >
          <GripVertical
            className="w-4 h-4"
            style={{ color: designTokens.colors.gray[4] }}
          />
        </div>
      )
    }, [handleDragStart, handleDragEnd])

    // Type renderer with color-coded tags
    const TypeRenderer = useCallback((params: ICellRendererParams<PerformanceTreeNode>) => {
      const data = params.data
      if (!data) return null

      let displayType: 'business' | 'performance_group' | 'performance_category' | 'performance_class' | 'performance' = 'performance_category'
      let linkType: string | undefined = data.linkType

      switch (data.type) {
        case 'business':
          displayType = 'business'
          break
        case 'performance_group':
          displayType = 'performance_group'
          break
        case 'performance_category':
          displayType = 'performance_category'
          break
        case 'performance_class':
          displayType = 'performance_class'
          break
        case 'performance':
          displayType = 'performance'
          linkType = data.linkType
          break
        default:
          displayType = 'performance_category'
      }
      const label = getPerformanceTypeLabel(displayType, linkType)

      const typeConfigs = {
        '사업': { color: 'purple' as const },
        '지표구분': { color: 'info' as const },
        '하위분류': { color: 'warning' as const },
        '성과지표': { color: 'success' as const },
        '세부지표': { color: 'error' as const },
        '연결세부지표': { color: 'error' as const }
      }

      const config = typeConfigs[label as keyof typeof typeConfigs] || { color: 'gray' as const }

      return (
        <Tag color={config.color} size="small">
          ● {label}
        </Tag>
      )
    }, [])

    // Custom name renderer with hierarchy indentation and expand/collapse icons
    const NameRenderer = useCallback((params: ICellRendererParams<PerformanceTreeNode>) => {
      const data = params.data
      if (!data) return null

      const depth = data.orgHierarchy.length - 1
      const indent = depth * 24
      const itemPath = data.orgHierarchy.join('|')
      const isCollapsed = collapsedItems.has(itemPath)
      const itemHasChildren = hasChildren(data, treeData)

      return (
        <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
          <div
            className={`flex items-center justify-center w-5 h-5 mr-2 rounded ${
              data.type === 'performance' ? '' : 'cursor-pointer hover:bg-gray-100'
            }`}
            onClick={(e) => {
              e.stopPropagation()
              onSetActionButtonClicked?.(true)
              setTimeout(() => onSetActionButtonClicked?.(false), 100)
              data.type !== 'performance' && itemHasChildren && toggleCollapse(itemPath)
            }}
            style={{
              visibility: data.type === 'performance' || itemHasChildren ? 'visible' : 'hidden',
              opacity: data.type === 'performance' || itemHasChildren ? 1 : 0
            }}
          >
            {data.type === 'performance' ? (
              <Circle
                className="w-3 h-3"
                style={{
                  color: designTokens.colors.gray[4],
                  fill: designTokens.colors.gray[4]
                }}
              />
            ) : itemHasChildren ? (
              isCollapsed ? (
                <ChevronDown
                  className="w-4 h-4"
                  style={{ color: designTokens.colors.primary.DEFAULT }}
                />
              ) : (
                <ChevronUp
                  className="w-4 h-4"
                  style={{ color: designTokens.colors.primary.DEFAULT }}
                />
              )
            ) : null}
          </div>

          <div>
            <div style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[6],
              fontWeight: 600
            }}>
              {data.name}
            </div>
          </div>
        </div>
      )
    }, [collapsedItems, hasChildren, treeData, toggleCollapse, onSetActionButtonClicked])

    const formatValue = (value: number | null) => {
      if (value === null) return '-'
      return new Intl.NumberFormat('ko-KR').format(value)
    }

    const formatRate = (rate: number | null) => {
      if (rate === null) return '-'
      return `${rate.toFixed(1)}%`
    }

    const ActionCellRenderer = useCallback((params: ICellRendererParams<PerformanceTreeNode>) => {
      const data = params.data
      if (!data) return null

      const { type, id } = data

      const handleCellClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
      }

      if (type === 'business') {
        return (
          <div className="flex items-center h-full" onClick={handleCellClick}>
            <Button
              size="small"
              color="white"
              onClick={(e) => {
                e.stopPropagation()
                onCreatePerformanceIndicator?.(id)
              }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              지표구분 생성
            </Button>
          </div>
        )
      }

      if (type === 'performance_group') {
        return (
          <div className="flex items-center h-full" onClick={handleCellClick}>
            <Button
              size="small"
              color="white"
              onClick={(e) => {
                e.stopPropagation()
                onCreateCategory?.(id)
              }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              하위분류 생성
            </Button>
          </div>
        )
      }

      if (type === 'performance_category') {
        return (
          <div className="flex items-center gap-1 h-full" onClick={handleCellClick}>
            <Button
              size="small"
              color="white"
              onClick={(e) => {
                e.stopPropagation()
                onCreateCategory?.(id)
              }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              하위분류 생성
            </Button>
            <Button
              size="small"
              color="white"
              onClick={(e) => {
                e.stopPropagation()
                onCreatePerformanceClass?.(id)
              }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              성과지표 생성
            </Button>
          </div>
        )
      }

      if (type === 'performance_class') {
        return (
          <div className="flex items-center gap-1 h-full" onClick={handleCellClick}>
            <Button
              size="small"
              color="white"
              onClick={(e) => {
                e.stopPropagation()
                onAddPerformance?.(id)
              }}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              세부지표 추가
            </Button>
          </div>
        )
      }

      return null
    }, [onCreatePerformanceIndicator, onCreateCategory, onCreatePerformanceClass, onEditPerformanceClass, onAddPerformance])

    const columnDefs: ColDef[] = useMemo(() => [
      // Gripper Column (순서 변경)
      {
        headerName: '',
        field: 'gripper',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: GripperRenderer,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0
        } as any
      },
      {
        headerName: '이름',
        field: 'name',
        width: 450,
        cellRenderer: NameRenderer,
        sortable: false,
        filter: false,
        pinned: 'left'
      },
      {
        headerName: '번호',
        field: 'formular_key',
        flex: 1,
        width: 100,
        maxWidth: 100,
        valueFormatter: (params) => params.data?.formular_key,
        sortable: false,
        filter: false,
        pinned: 'left'
      },
      {
        headerName: '액션',
        field: 'actions',
        width: 300,
        cellRenderer: ActionCellRenderer,
        sortable: false,
        filter: false,
        pinned: 'left'
      },
      {
        headerName: '유형',
        field: 'type',
        flex: 1,
        cellRenderer: TypeRenderer,
        sortable: true,
        filter: false
      },
      {
        headerName: '목표값',
        field: 'targetValue',
        flex: 1,
        valueFormatter: (params) => formatValue(params.value),
        cellStyle: { textAlign: 'right' }
      },
      {
        headerName: '실적값',
        field: 'actualValue',
        flex: 1,
        valueFormatter: (params) => formatValue(params.value),
        cellStyle: { textAlign: 'right' }
      },
      {
        headerName: '달성율',
        field: 'achievementRate',
        flex: 1,
        valueFormatter: (params) => formatRate(params.value),
        cellStyle: ((params: any) => {
          const rate = params.value
          if (rate === null) return { textAlign: 'right' }

          let color = designTokens.colors.gray[6]
          if (rate >= 100) color = designTokens.colors.success.DEFAULT
          else if (rate >= 80) color = designTokens.colors.primary.DEFAULT
          else if (rate >= 60) color = designTokens.colors.warning.DEFAULT
          else color = designTokens.colors.error.DEFAULT

          return {
            textAlign: 'right',
            color: color,
            fontWeight: 600
          }
        }) as any
      },
      {
        headerName: '부서',
        field: 'department.name',
        flex: 1,
        sortable: true,
        filter: false
      },
      {
        headerName: '담당자',
        field: 'assignee.name',
        flex: 1,
        sortable: true,
        filter: false
      },
    ], [GripperRenderer, NameRenderer, ActionCellRenderer, TypeRenderer])

    // Loading state
    if (loading) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[4]
            }}
          >
            성과 데이터를 불러오는 중...
          </div>
        </div>
      )
    }

    // Error state
    if (error) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.error.DEFAULT
            }}
          >
            {error}
          </div>
        </div>
      )
    }

    // Empty state
    if (!data || data.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[4]
            }}
          >
            표시할 성과 데이터가 없습니다.
          </div>
        </div>
      )
    }

    return (
      <div
        ref={gridContainerRef}
        className="w-full relative"
        onDragOver={handleGridDragOver}
        onDrop={handleGridDrop}
        onDragLeave={handleGridDragLeave}
      >
        {/* 자동 스크롤 영역 표시 (드래그 중에만 표시) */}
        {dragState.isDragging && (
          <>
            {/* 상단 스크롤 영역 (헤더 높이 40px 고려) */}
            <div
              className="absolute left-0 right-0 h-[120px] pointer-events-none z-10"
              style={{
                top: '40px',
                background: 'linear-gradient(to bottom, rgba(108, 172, 228, 0.15) 0%, transparent 100%)'
              }}
            />
            {/* 하단 스크롤 영역 */}
            <div
              className="absolute bottom-0 left-0 right-0 h-[120px] pointer-events-none z-10"
              style={{
                background: 'linear-gradient(to top, rgba(108, 172, 228, 0.15) 0%, transparent 100%)'
              }}
            />
          </>
        )}

        <DataGrid
          rowData={filteredData}
          columnDefs={columnDefs}
          height="calc(100vh - 210px)"
          rowSelection="multiple"
          animateRows={true}
          headerHeight={40}
          rowHeight={42}
          enableCellCopy={true}
          cellCopyExcludeColumns={['actions', 'gripper']}
          getRowId={(params) => params.data.id}
          onRowClicked={(event) => {
            // gripper 또는 actions 컬럼 클릭 시 행 클릭 이벤트 무시
            const target = event.event?.target as HTMLElement | null
            if (target) {
              // gripper-handle 클릭 시 무시
              if (target.closest('.gripper-handle')) {
                return
              }
              // actions 버튼 클릭 시 무시
              if (target.closest('button')) {
                return
              }
            }
            if (event.data) {
              onRowClick?.(event.data)
            }
          }}
          enableRangeSelection={true}
          rangeSelectionExcludeColumns={['actions', 'gripper']}
          suppressRowClickSelection={true}
          rangeCopyFormatter={(colId, value) => {
            if (colId === 'type') {
              const typeLabels: Record<string, string> = {
                'business': '사업',
                'performance_group': '지표구분',
                'performance_category': '하위분류',
                'performance_class': '성과지표',
                'performance': '세부지표'
              }
              return typeLabels[value] || value || '-'
            }
            if (colId === 'targetValue' || colId === 'actualValue' || colId === 'achievementRate' || colId === 'sort_key') {
              if (value === null || value === undefined) return ''
              return value.toString()
            }
            if (value === null || value === undefined) return '-'
            return value.toString()
          }}
        />
      </div>
    )
  }
)

PerformanceHierarchyTable.displayName = 'PerformanceHierarchyTable'

export default PerformanceHierarchyTable
